import React from "react";
import {
  useTheme,
  Box,
  Link,
  FormHelperText,
  FormControl,
  InputLabel,
  Input,
} from "@mui/material";

import { Controlled as CodeMirror } from "react-codemirror2";
import "codemirror/mode/markdown/markdown";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/material-darker.css";

function usePixelRatio() {
  const [pixelRatio, updatePixelRatio] = React.useState(
    window.devicePixelRatio
  );

  React.useEffect(() => {
    const handleResize = (evt) => {
      updatePixelRatio(window.devicePixelRatio);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return pixelRatio;
}

const CodeMirrorInput = React.forwardRef(function CodeMirrorInput(
  { value, onChange, editorDidMount },
  ref
) {
  const theme = useTheme();
  const pixelRatio = usePixelRatio();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        width: "100%",
        overflow: "auto",
      }}
      ref={ref}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          width: "100%",
          overflow: "auto",

          "& .CodeMirror": {
            height: "auto",
            fontSize: 14,
            lineHeight: 1.4,
            fontFamily: "'Roboto Mono', monospace",
          },

          "& .CodeMirror-cursors": {
            borderLeftWidth: `calc(1px * ${1 / pixelRatio} + 0.5)`,
          },
        }}
        component={CodeMirror}
        value={value}
        options={{
          mode: "markdown",
          theme: theme.palette.mode === "light" ? "default" : "material-darker",
          lineNumbers: true,
          lineWrapping: true,
          viewportMargin: Infinity,
        }}
        onBeforeChange={(editor, data, value) => onChange(value)}
        onChange={(editor, data, value) => {}}
        editorDidMount={editorDidMount}
        onDrop={(editor, event) => {
          event.codemirrorIgnore = 1;
        }}
      />
    </Box>
  );
});

export default function MarkdownInput({
  input,
  meta,
  helperText,
  required,
  label,
  ...props
}) {
  const error = Boolean(meta.touched && (meta.error || meta.submitError));
  const _helperText = error ? meta.error ?? meta.submitError : helperText;

  return (
    <FormControl error={error} required={required} fullWidth>
      {label && (
        <InputLabel focused={meta.active} shrink>
          {label}
        </InputLabel>
      )}

      <Input inputComponent={CodeMirrorInput} {...input} inputProps={props} />

      <FormHelperText sx={{ display: "flex" }}>
        <Box component="span" sx={{ flexGrow: 1 }}>
          {_helperText}
        </Box>

        <Box component="span" sx={{ ml: 1, mr: 1 }}>
          <Link
            href="https://github.com/adam-p/markdown-here/wiki/Markdown-Cheatsheet"
            target="_blank"
            rel="noopener noreferrer"
          >
            Markdown help
          </Link>
        </Box>
      </FormHelperText>
    </FormControl>
  );
}
